import * as React from "react";
import { Download } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { SnapshotCollection } from "@/modules/snapshots/snapshotCollection.types";
import * as analytics from "@/services/analytics";
import {
  SNAPSHOT_COLLECTION_EXPORT_OPTIONS,
  canExportAllCollections,
  getAllSnapshotCollectionsExportPayload,
  getSnapshotCollectionExportLabel,
  getSnapshotCollectionExportPayload,
} from "./exportOutcomes.utils";
import { useSnapshotExport } from "./useSnapshotExport";
import type { SnapshotViewConfiguration } from "@/modules/snapshots/snapshotViews.types";

interface Props {
  snapshotCollection: SnapshotCollection;
  filteredOutcomeIds?: string[];
  subcollectionId?: string;
  subcollectionName?: string;
  label?: string;
  size?: "sm" | "xs" | "lg";
  className?: string;
  disabled?: boolean;
  contractFamilyProjection?: boolean;
  snapshotViewId?: string;
  snapshotViewConfiguration?: SnapshotViewConfiguration;
}

export const SnapshotCollectionsOutcomesExport: React.FC<Props> = ({
  snapshotCollection,
  filteredOutcomeIds,
  subcollectionId,
  subcollectionName,
  size,
  label = "Export SnapShots",
  className,
  disabled = false,
  contractFamilyProjection = false,
  snapshotViewId,
  snapshotViewConfiguration,
}) => {
  const { isExporting, startSnapshotExport } = useSnapshotExport();

  const handleClick = (payload: Parameters<typeof startSnapshotExport>[0]) => () => {
    analytics.snapshotExport();
    analytics.snapshotCollectionExport(snapshotCollection.name);
    void startSnapshotExport(payload);
  };

  const isFiltered = !!filteredOutcomeIds?.length;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant="default" size={size} className={className} disabled={disabled || isExporting}>
          <Download className="mr-2 h-4 w-4" />
          {isExporting ? "Preparing..." : label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {canExportAllCollections({
          isFamilyCollection: snapshotCollection.is_family_collection,
          contractFamilyProjection,
          snapshotViewId,
        }) &&
          SNAPSHOT_COLLECTION_EXPORT_OPTIONS.map(option => (
            <DropdownMenuItem
              key={`all-${option.format}`}
              onClick={handleClick(
                getAllSnapshotCollectionsExportPayload(snapshotCollection.business_report_id, option.format),
              )}
              className="cursor-pointer"
              disabled={disabled || isExporting}
            >
              Export all collections to {option.label}
            </DropdownMenuItem>
          ))}

        {SNAPSHOT_COLLECTION_EXPORT_OPTIONS.map(option => (
          <DropdownMenuItem
            key={`collection-${option.format}`}
            onClick={handleClick(
              snapshotViewId && snapshotViewConfiguration
                ? {
                    snapshot_view_id: snapshotViewId,
                    view_configuration: snapshotViewConfiguration,
                    client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
                    format: option.format,
                  }
                : getSnapshotCollectionExportPayload({
                    snapshotCollectionId: snapshotCollection.id,
                    businessReportId: snapshotCollection.business_report_id,
                    format: option.format,
                    filteredOutcomeIds,
                    subcollectionId,
                    contractFamilyProjection,
                  }),
            )}
            className="cursor-pointer"
            disabled={disabled || isExporting}
          >
            {snapshotViewId
              ? `Export current View to ${option.label}`
              : getSnapshotCollectionExportLabel({
                  collectionName: snapshotCollection.name,
                  subcollectionName,
                  formatLabel: option.label,
                  isFiltered,
                })}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
